import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { getAdminDashboard } from '../../api/admin';
import { listPlanOrders } from '../../api/admin-plan';
import {
  Building2,
  CreditCard,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

const statusLabels: Record<string, string> = {
  pending: '待支付',
  paid: '已支付',
  cancelled: '已取消',
};

const statusVariants: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger'> = {
  pending: 'warning',
  paid: 'success',
  cancelled: 'default',
};

export function DashboardPage(): React.JSX.Element {
  const { data: dashboard } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: getAdminDashboard,
  });

  const { data: allOrders } = useQuery({
    queryKey: ['admin', 'plan-orders', 'all'],
    queryFn: () => listPlanOrders({ pageSize: 100 }),
  });

  const totalRevenue = allOrders?.items
    .filter((o) => o.order.status === 'paid')
    .reduce((sum, o) => sum + (o.order.amountCents || 0) / 100, 0) ?? 0;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1E293B]">平台总览</h1>
        <p className="mt-1 text-sm text-[#64748B]">
          SaaS 平台核心数据与待办事项
        </p>
      </div>

      {/* 核心指标 */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EFF6FF]">
              <Building2 className="h-6 w-6 text-[#2563EB]" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-[#1E293B]">
                {dashboard?.shops.total ?? '-'}
              </div>
              <div className="text-sm text-[#64748B]">租户门店</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FFFBEB]">
              <Clock className="h-6 w-6 text-[#D97706]" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-[#1E293B]">
                {dashboard?.orders.pending ?? '-'}
              </div>
              <div className="text-sm text-[#64748B]">待确认订单</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F0FDF4]">
              <CheckCircle2 className="h-6 w-6 text-[#16A34A]" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-[#1E293B]">
                {dashboard?.orders.paid ?? '-'}
              </div>
              <div className="text-sm text-[#64748B]">已支付订单</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F0FDF4]">
              <TrendingUp className="h-6 w-6 text-[#16A34A]" />
            </div>
            <div>
              <div className="text-2xl font-semibold text-[#16A34A]">
                ¥{totalRevenue.toFixed(0)}
              </div>
              <div className="text-sm text-[#64748B]">平台收入</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 门店状态分布 + 待办事项 */}
      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>门店状态分布</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#64748B]">试用中</span>
                <span className="text-sm font-medium text-[#1E293B]">
                  {dashboard?.shops.trial ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#64748B]">正式使用</span>
                <span className="text-sm font-medium text-[#16A34A]">
                  {dashboard?.shops.active ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#64748B]">只读期</span>
                <span className="text-sm font-medium text-[#D97706]">
                  {dashboard?.shops.readonly ?? 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#64748B]">已过期</span>
                <span className="text-sm font-medium text-[#DC2626]">
                  {dashboard?.shops.expired ?? 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              待办事项
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg bg-[#F8FAFC] p-4">
                <div>
                  <div className="font-medium text-[#1E293B]">待确认套餐订单</div>
                  <div className="text-sm text-[#64748B]">需要人工确认线下收款</div>
                </div>
                <div className="text-2xl font-semibold text-[#D97706]">
                  {dashboard?.orders.pending ?? 0}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-[#F8FAFC] p-4">
                <div>
                  <div className="font-medium text-[#1E293B]">订阅即将到期</div>
                  <div className="text-sm text-[#64748B]">30天内到期的门店</div>
                </div>
                <div className="text-2xl font-semibold text-[#DC2626]">
                  {dashboard?.subscriptions.expiringIn30Days ?? 0}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 最近订单 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            最近套餐订单
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allOrders?.items.slice(0, 5).map((item) => (
            <div
              key={item.order.id}
              className="flex items-center justify-between border-b border-[#F1F5F9] py-3 last:border-0"
            >
              <div>
                <div className="font-medium text-[#1E293B]">{item.shop.name}</div>
                <div className="text-sm text-[#64748B]">{item.plan.name}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-medium text-[#1E293B]">
                  ¥{(item.order.amountCents / 100).toFixed(0)}
                </span>
                <Badge variant={statusVariants[item.order.status] || 'default'}>
                  {statusLabels[item.order.status] || item.order.status}
                </Badge>
              </div>
            </div>
          )) ?? (
            <p className="py-4 text-center text-sm text-[#94A3B8]">暂无订单数据</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
